import {
  serverTimestamp,
  Timestamp,
  FieldValue,
  GeoPoint,
  DocumentReference,
} from 'firebase/firestore'

export class FirestoreTransformer {
  /**
   * Elimina campos `undefined` recursivamente (Firestore no admite `undefined`).
   *
   * Contrato (cristalizado en ADR-001 no-SEC, 2026-05-19):
   * - Preserva instancias de tipos especiales del SDK Firebase
   *   (`Timestamp`, `FieldValue`, `GeoPoint`, `DocumentReference`) como
   *   referencias intactas, sin iterarlas como objetos planos.
   * - Normaliza `Date` JS a `Timestamp.fromDate(value)` para garantizar
   *   wire format `timestampValue`.
   * - Nested arrays se convierten a objetos con keys numéricas (Firestore
   *   no soporta arrays anidados).
   *
   * Cross-repo: mismo contrato que `quizoteca-admin/src/services/firestore/firestoreTransformer.ts`
   * (ADR-002 admin, 2026-05-19). Cierre preventivo del bug latente clonado
   * literalmente desde el admin.
   */
  removeUndefinedFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const cleaned: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(obj)) {
      if (value === undefined) {
        continue
      } else if (value === null) {
        cleaned[key] = value
      } else if (
        value instanceof Timestamp ||
        value instanceof FieldValue ||
        value instanceof GeoPoint ||
        value instanceof DocumentReference
      ) {
        // Preservar instancias de tipos especiales del SDK Firebase como
        // referencias intactas (ADR-001 no-SEC).
        cleaned[key] = value
      } else if (value instanceof Date) {
        // Normalizar Date JS → Timestamp (defense in depth).
        cleaned[key] = Timestamp.fromDate(value)
      } else if (Array.isArray(value)) {
        cleaned[key] = value.map((item) => {
          if (Array.isArray(item)) {
            const arrObj: Record<string, unknown> = {}
            item.forEach((val, idx) => {
              arrObj[String(idx)] =
                typeof val === 'object' && val !== null
                  ? this.removeUndefinedFields(val as Record<string, unknown>)
                  : val
            })
            return arrObj
          }
          return typeof item === 'object' && item !== null
            ? this.removeUndefinedFields(item as Record<string, unknown>)
            : item
        })
      } else if (typeof value === 'object' && value !== null) {
        cleaned[key] = this.removeUndefinedFields(value as Record<string, unknown>)
      } else {
        cleaned[key] = value
      }
    }

    return cleaned as Partial<T>
  }

  addTimestamps(data: Record<string, unknown>, isUpdate = false): Record<string, unknown> {
    const result: Record<string, unknown> = { ...data, updated_at: serverTimestamp() }

    if (!isUpdate) {
      result.created_at = serverTimestamp()
    }

    return result
  }

  prepareForCreate<T extends Record<string, unknown>>(data: T): Record<string, unknown> {
    const cleaned = this.removeUndefinedFields(data)
    return this.addTimestamps(cleaned as Record<string, unknown>, false)
  }

  prepareForUpdate<T extends Record<string, unknown>>(data: T): Record<string, unknown> {
    const cleaned = this.removeUndefinedFields(data)
    return this.addTimestamps(cleaned as Record<string, unknown>, true)
  }
}

export const firestoreTransformer = new FirestoreTransformer()
